import React, { useState, useEffect, useCallback } from 'react'; // Import useCallback
import { useParams } from 'react-router-dom'; // Import useParams to get URL parameters
import api from '../utils/api';

const MemoManagement = () => {
  const { memberId } = useParams(); // Get memberId from URL
  const [memos, setMemos] = useState([]);
  const [memoForm, setMemoForm] = useState({
    memoId: null,
    memberId: memberId || '', // Pre-populate memberId from URL
    memoTitle: '',
    memoText: '',
    createdBy: '',
    updatedBy: ''
  });
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all memos for the specific memberId
  const fetchMemos = useCallback(async () => {
    try {
      const res = await api.get('/api/member_memos', { params: { memberId } });
      setMemos(res.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch member memos.');
    }
  }, [memberId]); // Include memberId as a dependency

  // Check if memberId is missing or invalid
  useEffect(() => {
    if (!memberId) {
      setError('Member ID is missing. Please provide a valid Member ID.');
      return;
    }
    fetchMemos(); // Call fetchMemos
  }, [memberId, fetchMemos]); // Include fetchMemos in the dependency array

  // Handle form input
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setMemoForm((prev) => ({ ...prev, [name]: value }));
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/api/member_memos/${memoForm.memoId}`, memoForm);
      } else {
        await api.post('/api/member_memos', memoForm);
      }
      setMemoForm({
        memoId: null,
        memberId: memberId, // Keep memberId from URL
        memoTitle: '',
        memoText: '',
        createdBy: '',
        updatedBy: ''
      });
      setEditing(false);
      fetchMemos();
    } catch (err) {
      setError('Failed to save memo.');
    }
  };

  // Edit existing memo
  const handleEdit = (memo) => {
    setMemoForm(memo);
    setEditing(true);
  };

  // Delete memo
  const handleDelete = async (id) => {
    if (window.confirm('Delete this memo?')) {
      try {
        await api.delete(`/api/member_memos/${id}`);
        fetchMemos();
      } catch (err) {
        setError('Failed to delete memo.');
      }
    }
  };

  return (
    <div className="container py-4">
      <h2 className="mb-4">Member Memo Management for Member ID: {memberId}</h2> {/* Updated header */}

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Memo Form */}
      <form onSubmit={handleSubmit} className="row g-2 align-items-end mb-3">
        <div className="col-md-2">
          <input
            type="number"
            name="memberId"
            className="form-control"
            placeholder="Member ID"
            value={memoForm.memberId}
            onChange={handleFormChange}
            required
            readOnly // Gray out the field
          />
        </div>
        <div className="col-md-3">
          <input
            type="text"
            name="memoTitle"
            className="form-control"
            placeholder="Memo Title"
            value={memoForm.memoTitle}
            onChange={handleFormChange}
          />
        </div>
        <div className="col-md-4">
          <input
            type="text"
            name="memoText"
            className="form-control"
            placeholder="Memo Text"
            value={memoForm.memoText}
            onChange={handleFormChange}
            required
          />
        </div>
        <div className="col-md-3">
          <button type="submit" className="btn btn-primary me-2">
            {editing ? 'Update Memo' : 'Add Memo'}
          </button>
          {editing && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setEditing(false);
                setMemoForm({
                  memoId: null,
                  memberId: memberId, // Keep memberId from URL
                  memoTitle: '',
                  memoText: '',
                  createdBy: '',
                  updatedBy: ''
                });
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Memo Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-hover table-sm align-middle">
          <thead className="table-light">
            <tr>
              <th>ID</th>
              <th>Member ID</th>
              <th>Title</th>
              <th>Text</th>
              <th>Created By</th>
              <th>Updated By</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {memos.map((m) => (
              <tr key={m.memoId}>
                <td>{m.memoId}</td>
                <td>{m.memberId}</td>
                <td>{m.memoTitle}</td>
                <td>{m.memoText}</td>
                <td>{m.createdBy || ''}</td>
                <td>{m.updatedBy || ''}</td>
                <td>{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</td>
                <td>{m.updatedAt ? new Date(m.updatedAt).toLocaleString() : ''}</td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-primary me-1"
                    onClick={() => handleEdit(m)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(m.memoId)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {memos.length === 0 && (
              <tr>
                <td colSpan="9" className="text-center text-muted">
                  No memos found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MemoManagement;